import { ApplicationProperty } from '@processpuzzle/base-widget';
import { BaseEntityRestService } from '@processpuzzle/base-entity';
import { BizApplicationPropertyMapper } from './biz-application-property.mapper';

/**
 * The application properties, read from and written to **this repository's own backend** rather
 * than to the testbed application's json-server.
 *
 * Core's `ApplicationPropertyService` resolves `THIRD_PARTY_ROOT`, which in the testbed frontend is
 * the json-server mock it is meant to be. Biz's `config.stage.json` and `config.prod.json` used to
 * copy that key verbatim from testbed's configuration, which aimed the marketing site's like button
 * at `https://testbed.stage.processpuzzle.de/third-party/application-properties` — a cross-origin
 * request to a host that answers `503`, and whose nginx sets no `Access-Control-Allow-Origin`
 * anyway because nothing had ever called it from another origin. Every page load of
 * `stage.processpuzzle.de` therefore failed that request and popped an error snackbar at the
 * visitor.
 *
 * `SUBSCRIPTION_SERVICE_ROOT` is reused rather than a key of its own being added: it is already
 * `/api` in `config.common.json` (inherited by ci, stage and prod) and the backend's own address in
 * `config.dev.json`, and it is where `processpuzzle-biz-backend` answers generally. A new key would
 * be a fourth place to keep in step for no gain, and `WIDGET_SERVICE_ROOT` was rejected outright:
 * it is documented as organization-scoped, and `serviceRootOf` falls it back to
 * `BACKEND_SERVICE_ROOT`, which no Biz configuration sets — so a deployment that forgot to set it
 * would silently build a root-relative URL, hit nginx's SPA fallback and try to parse `index.html`.
 *
 * **Construct this inside an injection context.** `BaseEntityRestService` reads
 * `RUNTIME_CONFIGURATION` and `HttpClient` in field initialisers, so `new BizApplicationPropertyService(...)`
 * outside one throws. The provider in `app.config.ts` uses `useFactory`, which runs in one.
 */
export class BizApplicationPropertyService extends BaseEntityRestService<ApplicationProperty> {
  constructor(protected override entityMapper: BizApplicationPropertyMapper) {
    super(entityMapper, 'SUBSCRIPTION_SERVICE_ROOT', 'application-properties');
  }
}
